import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import {
  patchConversation,
  type Conversation,
  type ConversationFlagsPatch,
  type PagedConversations,
} from '@/features/conversations/conversations.api';
import { conversationsKeys } from '@/features/conversations/keys';

interface FlagsVariables {
  conversationId: string;
  /** Partial flags body — at least one field (enforced server-side; callers
   *  always send exactly the field their menu item toggles). */
  patch: ConversationFlagsPatch;
}

interface InfiniteSnapshot {
  queryKey: readonly unknown[];
  data: { pages: PagedConversations[]; pageParams: unknown[] } | undefined;
}

interface FlagsMutationContext {
  /** Snapshots of every `['conversations', 'list', ...]` cache slot we
   *  patched, so onError can roll them all back — same approach as
   *  `useReadConversation`. */
  snapshots: InfiniteSnapshot[];
  /** Snapshot of the `['conversations', 'detail', id]` slot (the chat
   *  header's query), rolled back independently of the lists. */
  previousDetail: Conversation | undefined;
}

/**
 * Mirror a `ConversationFlagsPatch` onto a cached `Conversation` row — the
 * optimistic projection of what the server will persist.
 *
 * `pinned: true` fakes `pinned_at` with the client clock ONLY when the row
 * isn't already pinned (mirroring the server's idempotent-pin rule, so a
 * repeat pin can't visually reshuffle the pinned block); the server's
 * authoritative timestamp replaces it on the settle-invalidate refetch.
 */
function applyFlagsPatch(
  conversation: Conversation,
  patch: ConversationFlagsPatch,
): Conversation {
  const next = { ...conversation };
  if (patch.pinned !== undefined) {
    next.pinned_at = patch.pinned
      ? (conversation.pinned_at ?? new Date().toISOString())
      : null;
  }
  if (patch.marked_unread !== undefined) {
    next.marked_unread = patch.marked_unread;
  }
  if (patch.tags !== undefined) {
    next.tags = patch.tags;
  }
  return next;
}

/**
 * Toggle a conversation's GLOBAL flags — pin / mark-unread / business tag —
 * via `PATCH /conversations/{id}`.
 *
 * Optimistic strategy (the flags live on the `ConversationOut` embedded in
 * the infinite list pages AND in the detail slot, so both are patched — same
 * cache-walk as `useReadConversation`):
 *
 * - **Flags flip in place immediately**; on error every snapshot rolls back
 *   and a quiet toast reports the failure.
 * - **Ordering is deliberately NOT re-sorted locally.** The server is the
 *   single ordering authority (the four-zone default on the "All" tab —
 *   cuci → pinned → unread → read — and the classic pinned-then-recency on
 *   the `view=unread_pin` / `view=cuci` tabs); duplicating its comparators
 *   here would be a second implementation that can drift. Instead
 *   `onSettled` invalidates the lists — the refetch
 *   re-asserts server order within one round-trip. Chosen as the simpler
 *   correct option: a pin is a rare, deliberate action, so one refetch is
 *   cheap and the sub-second "flag flipped but row not yet moved" window is
 *   fine (the pin icon already shows the action took effect).
 */
export function useConversationFlags() {
  const queryClient = useQueryClient();

  return useMutation<Conversation, unknown, FlagsVariables, FlagsMutationContext>({
    mutationFn: ({ conversationId, patch }) =>
      patchConversation(conversationId, patch),

    onMutate: async ({ conversationId, patch }) => {
      // Cancel in-flight fetches so the optimistic patch isn't immediately
      // overwritten by stale server data.
      await queryClient.cancelQueries({ queryKey: conversationsKeys.lists() });
      await queryClient.cancelQueries({
        queryKey: conversationsKeys.detail(conversationId),
      });

      // Snapshot + patch every cached list (the infinite-query envelope is
      // `{ pages, pageParams }`, not a flat list — hence the manual walk).
      const snapshots: InfiniteSnapshot[] = queryClient
        .getQueriesData<{ pages: PagedConversations[]; pageParams: unknown[] }>({
          queryKey: conversationsKeys.lists(),
        })
        .map(([queryKey, data]) => ({ queryKey, data }));

      for (const { queryKey, data } of snapshots) {
        if (!data) continue;
        queryClient.setQueryData(queryKey, {
          ...data,
          pages: data.pages.map((page) => ({
            ...page,
            items: page.items.map((c) =>
              c.id === conversationId ? applyFlagsPatch(c, patch) : c,
            ),
          })),
        });
      }

      // Snapshot + patch the detail slot the chat header reads.
      const previousDetail = queryClient.getQueryData<Conversation>(
        conversationsKeys.detail(conversationId),
      );
      if (previousDetail) {
        queryClient.setQueryData(
          conversationsKeys.detail(conversationId),
          applyFlagsPatch(previousDetail, patch),
        );
      }

      return { snapshots, previousDetail };
    },

    onError: (_error, { conversationId }, context) => {
      if (!context) return;
      for (const { queryKey, data } of context.snapshots) {
        queryClient.setQueryData(queryKey, data);
      }
      if (context.previousDetail !== undefined) {
        queryClient.setQueryData(
          conversationsKeys.detail(conversationId),
          context.previousDetail,
        );
      }
      toast("Couldn't update conversation flags");
    },

    onSettled: (_data, _error, { conversationId }) => {
      // Server truth wins — and, crucially, the list refetch re-asserts the
      // server's ordering AND the `view=` membership the flag flip may have
      // changed: the four-zone default on the "All" tab (cuci → pinned →
      // unread → read) and the classic pinned-then-recency on the view tabs
      // (see the hook doc above for why we never re-sort locally).
      void queryClient.invalidateQueries({ queryKey: conversationsKeys.lists() });
      void queryClient.invalidateQueries({
        queryKey: conversationsKeys.detail(conversationId),
      });
    },
  });
}
